<template>
  <div class="school-change">
    <div class="back-row" @click="goBack">
      <i class="el-icon-arrow-left"></i>
      <span>Back to Service Tickets</span>
    </div>

    <div class="header">
      <p class="title">School Change Request</p>
      <p class="subtitle">
        Use this ticket to change the school linked to one of your
        registrations. Our staff will verify the new school and update the
        relevant records after payment is confirmed.
      </p>
      <div class="fee-box">
        <div>
          <div class="fee-label">Service fee</div>
          <div class="fee-why" @click="showWhy = !showWhy">
            Why is there a fee?
            <i :class="showWhy ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </div>
        </div>
        <div class="fee-amount">USD 40</div>
      </div>
      <div v-if="showWhy" class="why-panel">
        Changing a school is reviewed manually. Staff must verify the new
        school, update the registration across applicable systems, and confirm
        the completed change with you. The fee covers that work.
      </div>
    </div>

    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-position="top"
      class="form-body">
      <el-form-item label="Which registration should be updated?" prop="application_id">
        <el-select
          v-model="form.application_id"
          placeholder="Select a registration"
          style="width: 100%;"
          :loading="appsLoading"
          @change="onApplicationChange">
          <el-option
            v-for="app in applications"
            :key="app._id.$id"
            :label="applicationLabel(app)"
            :value="app._id.$id">
          </el-option>
        </el-select>
        <p v-if="!appsLoading && !applications.length" class="hint">
          No registrations found on your account.
        </p>
      </el-form-item>

      <div v-if="form.application_id" class="current-school">
        <div class="current-school-title">Currently on file</div>
        <div v-loading="currentSchoolLoading" class="current-school-value">
          {{ currentSchoolName || 'School information unavailable' }}
        </div>
      </div>

      <div class="section-title">New school</div>
      <el-form-item label="Official School Name (English)" prop="new_school_name_en">
        <el-input
          v-model="form.new_school_name_en"
          placeholder="Enter the school's full official name">
        </el-input>
      </el-form-item>

      <el-form-item label="Official School Name (Native Language, optional)" prop="new_school_name_native">
        <el-input
          v-model="form.new_school_name_native"
          placeholder="Enter the official name in the school's local language">
        </el-input>
      </el-form-item>

      <div class="row">
        <el-form-item label="Country / Region" prop="new_school_country">
          <el-input
            v-model="form.new_school_country"
            placeholder="e.g. United States">
          </el-input>
        </el-form-item>
        <el-form-item label="Official School Website (optional)" prop="new_school_website">
          <el-input
            v-model="form.new_school_website"
            placeholder="https://www.school.edu">
          </el-input>
        </el-form-item>
      </div>

      <el-form-item label="Reason for change" prop="reason">
        <el-input
          v-model="form.reason"
          type="textarea"
          :rows="3"
          placeholder="Briefly explain why your school needs to be changed.">
        </el-input>
      </el-form-item>

      <div class="confirm-row" @click="confirmed = !confirmed">
        <img
          v-if="!confirmed"
          class="chk"
          :src="require('../../assets/image/unchecked.png')" />
        <img
          v-else
          class="chk"
          :src="require('../../assets/image/checked.png')" />
        <span>
          I have checked the new school information above. I understand that
          the USD 40 fee compensates staff time and will not be refunded once
          the change has been processed.
        </span>
      </div>

      <div class="actions">
        <el-button @click="goBack">Cancel</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">
          Submit &amp; Pay
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import {
  createServiceTicket,
  getAccount,
  getStripeUrlForServiceTicket,
  getStudentApplications,
} from '../../api/eae'
import { getUserId } from '../../utils/store'

export default {
  name: 'SchoolChangeTicket',
  data() {
    const validateWebsite = (rule, value, callback) => {
      if (!value) {
        callback()
        return
      }
      if (!/^https?:\/\//i.test(value.trim())) {
        callback(new Error('Please enter a complete URL beginning with http:// or https://'))
        return
      }
      callback()
    }

    return {
      showWhy: false,
      confirmed: false,
      submitting: false,
      appsLoading: false,
      currentSchoolLoading: false,
      applications: [],
      currentSchool: null,
      form: {
        application_id: '',
        new_school_name_en: '',
        new_school_name_native: '',
        new_school_country: '',
        new_school_website: '',
        reason: '',
      },
      rules: {
        application_id: [
          { required: true, message: 'Please select the registration to update', trigger: 'change' },
        ],
        new_school_name_en: [
          { required: true, message: 'Please enter the official school name', trigger: 'blur' },
        ],
        new_school_country: [
          { required: true, message: 'Please enter the country or region', trigger: 'blur' },
        ],
        new_school_website: [{ validator: validateWebsite, trigger: 'blur' }],
        reason: [
          { required: true, message: 'Please explain why the school needs to be changed', trigger: 'blur' },
        ],
      },
    }
  },
  computed: {
    selectedApp() {
      return this.applications.find((app) => app._id.$id === this.form.application_id) || null
    },
    currentSchoolId() {
      const app = this.selectedApp
      if (!app) return ''
      return this.extractId(app.sub_account_id) || this.extractId(app.account_id)
    },
    currentSchoolName() {
      const account = this.currentSchool
      const app = this.selectedApp
      return (
        (account && (account.account_nameEN || account.account_name)) ||
        (app && (app.sub_account_nameEN || app.account_nameEN || app.school)) ||
        ''
      )
    },
  },
  mounted() {
    this.fetchApplications()
  },
  methods: {
    extractId(value) {
      if (!value) return ''
      if (typeof value === 'string') return value
      return value.$id || (value._id && value._id.$id) || ''
    },
    fetchApplications() {
      const studentId = getUserId()
      if (!studentId) return
      this.appsLoading = true
      getStudentApplications(studentId)
        .then((res) => {
          const list = (res.data && res.data.data) || []
          this.applications = Array.isArray(list) ? list : []
        })
        .finally(() => {
          this.appsLoading = false
        })
    },
    applicationLabel(app) {
      const project = app.application_project || app.project_name || 'Registration'
      const school = app.sub_account_nameEN || app.account_nameEN || app.school || ''
      return school ? `${project} — ${school}` : project
    },
    onApplicationChange() {
      this.currentSchool = null
      const accountId = this.currentSchoolId
      if (!accountId) return
      this.currentSchoolLoading = true
      getAccount(accountId)
        .then((res) => {
          this.currentSchool = (res.data && res.data.data) || null
        })
        .finally(() => {
          this.currentSchoolLoading = false
        })
    },
    goBack() {
      this.$router.push({ path: '/home/serviceTickets' })
    },
    submit() {
      if (!this.confirmed) {
        this.$message.warning('Please confirm the new school information and the fee terms.')
        return
      }
      this.$refs.form.validate((valid) => {
        if (!valid) return
        const payload = {
          current_school_id: this.currentSchoolId,
          current_school_name: this.currentSchoolName,
          new_school_name_en: this.form.new_school_name_en.trim(),
          new_school_name_native: this.form.new_school_name_native.trim(),
          new_school_country: this.form.new_school_country.trim(),
          new_school_website: this.form.new_school_website.trim(),
          reason: this.form.reason.trim(),
        }
        this.submitting = true
        createServiceTicket(getUserId(), 'school_change', this.form.application_id, payload)
          .then((res) => {
            const data = res.data
            if (!data || data.code !== 0) {
              this.$message.error((data && data.msg) || 'Failed to create ticket')
              return Promise.reject()
            }
            const ticketId = data.data && data.data._id && data.data._id.$id
            if (!ticketId) {
              this.$message.error('Failed to create ticket (no id)')
              return Promise.reject()
            }
            return getStripeUrlForServiceTicket(ticketId)
          })
          .then((res) => {
            if (!res) return
            const data = res.data
            if (!data || data.code !== 0 || !data.data || !data.data.url) {
              this.$message.error((data && data.msg) || 'Failed to start payment')
              return
            }
            window.location.href = data.data.url
          })
          .finally(() => {
            this.submitting = false
          })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.school-change {
  padding: 24px 32px;
  max-width: 860px;
  margin: 0 auto;
}

.back-row {
  color: #ff8e3b;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 16px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.header {
  margin-bottom: 24px;

  .title {
    font-size: 22px;
    font-weight: bold;
    color: #ff8e3b;
    margin-bottom: 6px;
  }

  .subtitle {
    color: #666;
    font-size: 14px;
    line-height: 1.6;
  }
}

.fee-box {
  margin-top: 14px;
  padding: 14px 18px;
  background: #fff5f4;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .fee-label {
    color: #333;
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 2px;
  }

  .fee-why {
    color: #ff8e3b;
    font-size: 12px;
    cursor: pointer;
  }

  .fee-amount {
    font-size: 22px;
    font-weight: bold;
    color: #ff8e3b;
  }
}

.why-panel {
  margin-top: 10px;
  padding: 14px 18px;
  background: #f7f7f9;
  border-radius: 8px;
  font-size: 13px;
  color: #555;
  line-height: 1.6;
}

.form-body {
  margin-top: 8px;
}

.section-title {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  margin: 20px 0 10px;
}

.row {
  display: flex;
  gap: 20px;

  > .el-form-item {
    flex: 1;
  }
}

.current-school {
  margin: 0 0 20px;
  padding: 12px 16px;
  background: #fafafa;
  border-radius: 8px;

  .current-school-title {
    font-size: 12px;
    color: #999;
    margin-bottom: 6px;
  }

  .current-school-value {
    min-height: 20px;
    color: #333;
    font-size: 14px;
    font-weight: 500;
  }
}

.confirm-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 16px 0 24px;
  padding: 12px 14px;
  background: #fffbf5;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  line-height: 1.5;

  .chk {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 10px;
}

.hint {
  color: #999;
  font-size: 12px;
  margin-top: 4px;
}

@media (max-width: 640px) {
  .school-change {
    padding: 20px 16px;
  }

  .row {
    display: block;
  }
}
</style>
